import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Drawer, Icon, LabeledValue, Layout, Steps, Banner } from '@loadsmart/miranda-react'

import { useAnalytics } from 'core/analytics/useAnalytics'
import { RequestStatus } from 'core/domain/Request'
import { isHttpClientError } from 'core/infra/http/HttpClient'

import {
  buildValidationSchema,
  type OpenDockWarehouseDetails,
  type ScheduleFormValues,
} from '../../domain/Shipment'
import {
  type OpenDockWarehouseFormFieldValue,
  type OpenDockWarehouseFormFields,
} from '../../infra/ShipmentMapper'
import { formatScheduledAt } from '../../presenters/openDockAppointmentPresenter'
import { formatLoadTypeInfo } from '../../presenters/openDockLoadTypePresenter'
import { useCreateOpenDockWarehouseAppointment } from '../hooks/useCreateOpenDockWarehouseAppointment'
import { DynamicField, DynamicRefNumberField } from './DynamicField'
import { type Step } from './steps'

export type FormValues = {
  formFields: OpenDockWarehouseFormFieldValue[] | undefined
  customFormFields: OpenDockWarehouseFormFieldValue[] | undefined
  refNumbers: string | null | undefined
}

function setFormValuesToWarehouseFormFields(
  warehouseFormFields: OpenDockWarehouseFormFields,
  formValues: FormValues
) {
  const warehouseFormFieldsCopy = structuredClone(warehouseFormFields)

  if (warehouseFormFieldsCopy.formFields && typeof formValues.formFields !== 'undefined') {
    warehouseFormFieldsCopy.formFields.forEach((field, index) => {
      field.value = formValues?.formFields?.[index] ?? null
    })
  }

  if (
    warehouseFormFieldsCopy.customFormFields &&
    typeof formValues.customFormFields !== 'undefined'
  ) {
    warehouseFormFieldsCopy.customFormFields.forEach((field, index) => {
      field.value = formValues?.customFormFields?.[index] ?? null
    })
  }

  if (warehouseFormFieldsCopy.refNumbers) {
    warehouseFormFieldsCopy.refNumbers.value = formValues.refNumbers ?? null
  }

  return warehouseFormFieldsCopy
}

function joinIfIsArray(value?: string | string[] | null) {
  if (Array.isArray(value)) {
    return value.join(',')
  }
  return value
}

export type FlatennedFormValues = {
  [key: `custom-form-fields-${number}`]: OpenDockWarehouseFormFieldValue | undefined
} & {
  [key: `form-fields-${number}`]: OpenDockWarehouseFormFieldValue | undefined
} & {
  refNumbers?: string | null | undefined
}

export function flattenFields(
  customFormFields: OpenDockWarehouseFormFieldValue[] | undefined,
  formFields: OpenDockWarehouseFormFieldValue[] | undefined
) {
  const result: FlatennedFormValues = {}

  customFormFields?.forEach((value, index) => {
    result[`custom-form-fields-${index}`] = value
  })

  formFields?.forEach((value, index) => {
    result[`form-fields-${index}`] = value
  })

  return result
}

function unFlattenFields(flatennedFields: FlatennedFormValues): {
  customFormFields: OpenDockWarehouseFormFieldValue[] | undefined
  formFields: OpenDockWarehouseFormFieldValue[] | undefined
  refNumbers: string | null | undefined
} {
  const customFormFields: OpenDockWarehouseFormFieldValue[] = []
  const formFields: OpenDockWarehouseFormFieldValue[] = []

  Object.entries(flatennedFields).forEach(([key, value]) => {
    if (key.startsWith('custom-form-fields-')) {
      customFormFields.push(value ?? null)
    } else if (key.startsWith('form-fields-')) {
      formFields.push(value ?? null)
    }
  })

  return {
    customFormFields: customFormFields.length > 0 ? customFormFields : undefined,
    formFields: formFields.length > 0 ? formFields : undefined,
    refNumbers: flatennedFields.refNumbers,
  }
}

export function SendInfoForm({
  setStep,
  fields,
  facilityTimezone,
  warehouseDetails,
  warehouseFormFields,
  shipmentUUID,
  openDockCarrierId,
  stopIndex,
  onConfirm,
  onClose,
}: {
  setStep: (step: Step) => void
  fields: {
    staticFields: Partial<ScheduleFormValues>
    dynamicFields: Record<string, number | string | boolean | null>
  }
  facilityTimezone: string
  warehouseDetails: OpenDockWarehouseDetails
  warehouseFormFields: OpenDockWarehouseFormFields
  shipmentUUID: string
  openDockCarrierId: string | undefined
  stopIndex: number
  onConfirm: () => void
  onClose: () => void
}) {
  const { track } = useAnalytics()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const formikProperties = useFormik<FlatennedFormValues>({
    initialValues: {
      ...flattenFields(
        warehouseFormFields.customFormFields.map(field => field.value),
        warehouseFormFields.formFields.map(field => field.value)
      ),
      refNumbers: joinIfIsArray(warehouseFormFields.refNumbers?.value),
    },
    onSubmit: () => undefined,
    validationSchema: buildValidationSchema(warehouseFormFields),
    validateOnMount: true,
  })

  const { createAppointment, status, error } = useCreateOpenDockWarehouseAppointment()

  useEffect(() => {
    if (status === RequestStatus.Done) {
      track('Shipment details / Appointment scheduled', {
        appointment_type: 'appt',
        loading_type: fields.staticFields.loadType?.operation,
        hadId: 'NO',
        is_opendock: true,
      })

      toast.success('Scheduled successfully.')
      onConfirm()
      setStep('instructions')
      return
    }

    if (status === RequestStatus.Error) {
      const message = isHttpClientError(error)
        ? error.response?.data.message ?? 'Couldn’t schedule. Try again.'
        : 'Couldn’t schedule. Try again.'

      setSubmitError(message)
    }
  }, [fields.staticFields.loadType?.operation, onClose, onConfirm, setStep, status, track, error])

  return (
    <>
      <Drawer.Body>
        <Layout.Stack gap="spacing-4" paddingB="spacing-2">
          <Steps activeStep="send-info" style={{ gap: '0' }}>
            <Steps.Step completed name="schedule">
              Schedule
            </Steps.Step>
            <Steps.Step name="send-info">Send Info</Steps.Step>
          </Steps>
          {warehouseFormFields && (
            <>
              <LabeledValue>
                <LabeledValue.Label>Warehouse</LabeledValue.Label>
                <LabeledValue.Value>{warehouseDetails?.name}</LabeledValue.Value>
              </LabeledValue>
              <LabeledValue>
                <LabeledValue.Label>Load type</LabeledValue.Label>
                <LabeledValue.Value>
                  {formatLoadTypeInfo(fields.staticFields.loadType!, true)}
                </LabeledValue.Value>
              </LabeledValue>
              {fields.staticFields.loadType?.allowCarrierDockSelection && (
                <LabeledValue>
                  <LabeledValue.Label>Dock</LabeledValue.Label>
                  <LabeledValue.Value>{fields.staticFields.dock?.label}</LabeledValue.Value>
                </LabeledValue>
              )}
              <LabeledValue>
                <LabeledValue.Label>Scheduled At</LabeledValue.Label>
                <LabeledValue.Value>
                  {formatScheduledAt(
                    fields.staticFields.appointmentDate!,
                    fields.staticFields.appointmentTime!,
                    facilityTimezone
                  )}
                </LabeledValue.Value>
              </LabeledValue>
              {submitError && (
                <Banner variant="danger">
                  <Banner.Icon style={{ display: 'none' }} />
                  <Banner.Title>{submitError}</Banner.Title>
                </Banner>
              )}
              <Layout.Stack gap="spacing-4">
                {warehouseFormFields.formFields.map((field, index) => (
                  <DynamicField
                    key={`form-fields-${index}`}
                    field={field}
                    fieldName={`form-fields-${index}`}
                    formikProperties={formikProperties}
                    shipmentUUID={shipmentUUID}
                    facilityTimezone={facilityTimezone}
                  />
                ))}
                {warehouseFormFields.customFormFields.map((field, index) => (
                  <DynamicField
                    key={`custom-form-fields-${index}`}
                    field={field}
                    fieldName={`custom-form-fields-${index}`}
                    formikProperties={formikProperties}
                    shipmentUUID={shipmentUUID}
                    facilityTimezone={facilityTimezone}
                  />
                ))}
                {warehouseFormFields.refNumbers && (
                  <DynamicRefNumberField
                    field={warehouseFormFields.refNumbers}
                    fieldName={`refNumbers`}
                    formikProperties={formikProperties}
                  />
                )}
              </Layout.Stack>
            </>
          )}
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionTertiary
          onClick={() => setStep('schedule_date_and_time')}
          leading={<Icon name="arrow-left" color="color-text-primary" />}
          disabled={status === RequestStatus.Pending}
        >
          Back
        </Drawer.ActionTertiary>
        <Drawer.ActionPrimary
          disabled={!formikProperties.isValid}
          loading={status === RequestStatus.Pending}
          onClick={() => {
            const updatedWarehouseFormFields = setFormValuesToWarehouseFormFields(
              warehouseFormFields,
              unFlattenFields(formikProperties.values)
            )

            createAppointment(shipmentUUID, stopIndex, {
              staticFields: fields.staticFields as ScheduleFormValues,
              warehouseFormFields: updatedWarehouseFormFields,
              openDockCarrierId,
              facilityTimezone,
            })
          }}
        >
          Confirm appointment
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
